import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

interface FormulaToolbarProps {
  onInsert: (snippet: string, cursorIndex?: number) => void;
}

type ToolbarButton = {
  label: string;
  snippet: string;
  ariaLabel?: string;
  cursorIndex?: number;
};

const primaryButtons: ToolbarButton[] = [
  { label: "+", snippet: "[[ + ]]" },
  { label: "-", snippet: "[[ - ]]" },
  { label: "×", snippet: "[[ * ]]" },
  { label: "÷", snippet: "[[ / ]]" },
  { label: "=", snippet: "[[ = ]]" },
  { label: "≠", snippet: "[[ <> ]]" },
  { label: "<", snippet: "[[ < ]]" },
  { label: ">", snippet: "[[ > ]]" },
  { label: "≤", snippet: "[[ <= ]]" },
  { label: "≥", snippet: "[[ >= ]]" },
  { label: "^", snippet: "[[ ^ ]]" },
  { label: "()", snippet: "()", cursorIndex: 1 },
  { label: "[]", snippet: "[]", cursorIndex: 1 },
  { label: "{}", snippet: "{}", cursorIndex: 1 }
];

const extendedButtons: ToolbarButton[] = [
  { label: "√", snippet: "[[√()]]", cursorIndex: 2 },
  { label: "%", snippet: "[[ % ]]" },
  { label: "∑", snippet: "[[∑()]]", cursorIndex: 2, ariaLabel: "Sigma - Sum" },
  { label: "|x|", snippet: "[[|  |]]", cursorIndex: 2, ariaLabel: "قدر مطلق" },
  { label: "x²", snippet: " ^ 2" },
  { label: "x³", snippet: " ^ 3" },
  { label: "½", snippet: "[[ 1/2 ]]" },
  { label: "⅓", snippet: "[[ 1/3 ]]" },
  { label: "π", snippet: "[[ PI()]]", cursorIndex: 3 },
  { label: "LOG", snippet: "[[ LOG()]]", cursorIndex: 5 },
  { label: "LOG10", snippet: "[[ LOG10()]]", cursorIndex: 7 },
  { label: "EXP", snippet: "[[ EXP()]]", cursorIndex: 5 }
];

const tsqlButtons: ToolbarButton[] = [
  { label: "POWER", snippet: "POWER( , )", ariaLabel: "توان در T-SQL", cursorIndex: 6 },
  { label: "ROUND", snippet: "ROUND( , )", cursorIndex: 6 },
  { label: "SQRT", snippet: "SQRT()", cursorIndex: 5 },
  { label: "ABS", snippet: "ABS()", cursorIndex: 4 },
  { label: "AVG", snippet: "AVG()", cursorIndex: 4 },
  { label: "SUM", snippet: "SUM()", cursorIndex: 4 },
  { label: "MIN", snippet: "MIN()", cursorIndex: 4 },
  { label: "MAX", snippet: "MAX()", cursorIndex: 4 },
  { label: "COALESCE", snippet: "COALESCE( , )", cursorIndex: 9 }
];

const FormulaToolbar: React.FC<FormulaToolbarProps> = ({ onInsert }) => {
  const handleInsert = (snippet: string, cursorIndex?: number) => {
    onInsert(snippet, cursorIndex);
  };

  return (
    <div className="mb-3 space-y-3 rounded-lg border border-dashed border-slate-200 p-3">
      <div>
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span className="text-xs font-medium text-slate-700">
            نمادهای ریاضی
          </span>
          <span>برای درج روی هر نماد کلیک کنید</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {primaryButtons.map((btn) => (
            <Button
              key={btn.label}
              type="button"
              variant="secondary"
              size="sm"
              className="h-8 min-w-[42px] px-2 text-base"
              onClick={() => handleInsert(btn.snippet, btn.cursorIndex)}
            >
              {btn.label}
            </Button>
          ))}
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                نمادهای بیشتر
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] space-y-3" align="end">
              <p className="text-xs font-medium text-slate-600">
                نمادهای پیشرفته
              </p>
              <div className="grid grid-cols-4 gap-2">
                {extendedButtons.map((btn) => (
                  <Button
                    key={btn.label}
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-8 px-2 text-sm"
                    aria-label={btn.ariaLabel ?? btn.label}
                    onClick={() => handleInsert(btn.snippet, btn.cursorIndex)}
                  >
                    {btn.label}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div>
        <div className="text-[11px] font-medium text-slate-500">
          توابع پرکاربرد T-SQL
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {tsqlButtons.map((btn) => (
            <Button
              key={btn.label}
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-[11px]"
              aria-label={btn.ariaLabel ?? btn.label}
              onClick={() => handleInsert(btn.snippet, btn.cursorIndex)}
            >
              {btn.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FormulaToolbar;
